/*
 * Copyright (c) 2026-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

import { ICONS, type IconName } from './icons';

/**
 * One icon.
 *
 * Inherits `currentColor`, so it takes the colour of whatever it sits in and
 * needs no per-theme variant. Decorative by default: an icon beside a label
 * adds nothing for a screen reader, and an icon-only control passes a `label`
 * instead (SCR-9).
 */
export function Icon({
  name,
  size = 18,
  label,
  className,
}: {
  name: IconName;
  size?: number;
  label?: string;
  className?: string;
}) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      {...(label ? { role: 'img', 'aria-label': label } : { 'aria-hidden': true })}
      // nosemgrep: typescript.react.security.audit.react-dangerouslysetinnerhtml.react-dangerouslysetinnerhtml
      // The sprite, not user input. `ICONS` is a constant map of path data in
      // this repository and `IconName` is its key type, so nothing outside
      // these two files can reach here. The alternative is a component per
      // icon, which is eighty files to satisfy a pattern match.
      dangerouslySetInnerHTML={{ __html: ICONS[name] }}
    />
  );
}
